"use client";

import type { Priority } from "@orsane/schema/tasks";
import { Check } from "lucide-react";
import * as React from "react";
import { TASK_ROW_HEIGHT } from "@/common/constants";
import { priorities } from "@/lib/priority";
import { Command, CommandGroup, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface PrioritySelectProps {
    value: Priority | null;
    onChange: (values: Priority | null) => void;
    trigger: (content: React.ReactNode) => React.ReactElement;
}

const NoPrioritoyIcon = () => {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <title>None</title>
            <path
                d="M4.5 9C4.5 8.44772 4.05228 8 3.5 8C2.94772 8 2.5 8.44772 2.5 9V13C2.5 13.5523 2.94772 14 3.5 14C4.05228 14 4.5 13.5523 4.5 13V9Z"
                fill="currentColor"
                fillOpacity="0.3"
            />
            <path
                d="M8.5 6C8.5 5.44772 8.05228 5 7.5 5C6.94772 5 6.5 5.44772 6.5 6V13C6.5 13.5523 6.94772 14 7.5 14C8.05228 14 8.5 13.5523 8.5 13V6Z"
                fill="currentColor"
                fillOpacity="0.3"
            />
            <path
                d="M12.5 3C12.5 2.44772 12.0523 2 11.5 2C10.9477 2 10.5 2.44772 10.5 3V13C10.5 13.5523 10.9477 14 11.5 14C12.0523 14 12.5 13.5523 12.5 13V3Z"
                fill="currentColor"
                fillOpacity="0.3"
            />
        </svg>
    );
};

export function PrioritySelect({ value, onChange, trigger }: PrioritySelectProps) {
    const [open, setOpen] = React.useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
                render={trigger(
                    <>
                        {!value && (
                            <>
                                <NoPrioritoyIcon />
                                <div>None</div>
                            </>
                        )}
                        {!!value && (
                            <>
                                {priorities[value]?.icon}
                                <div>{priorities[value]?.label}</div>
                            </>
                        )}
                    </>
                )}
            />
            <PopoverContent sideOffset={-TASK_ROW_HEIGHT} align="start" className="w-full p-0 max-w-80">
                <Command value={value || "none"}>
                    <CommandList>
                        <CommandGroup>
                            <CommandItem
                                value="none"
                                onSelect={() => {
                                    onChange(null);
                                    setOpen(false);
                                }}
                            >
                                <div className="size-4" />
                                None
                            </CommandItem>
                            {Object.keys(priorities).map((priorityKey) => {
                                const priority = priorities[priorityKey as Priority];
                                return (
                                    <CommandItem
                                        value={priority.label}
                                        key={priority.label}
                                        onSelect={() => {
                                            onChange(priorityKey as Priority);
                                            setOpen(false);
                                        }}
                                    >
                                        {priority.icon}
                                        {priorityKey}
                                        <Check
                                            className={`text-foreground size-3.5 shrink-0 ml-auto ${value === priority.label ? "opacity-100" : "opacity-0"}`}
                                        />
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
